import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

if (!supabaseUrl || !supabaseKey) {
  console.error(
    "Supabase URL và Anon Key chưa được cấu hình trong biến môi trường."
  );
}

export const supabase = createClient(supabaseUrl, supabaseKey);

// Kiểu dữ liệu cho categories
export type Category = {
  id: string;
  slug: string;
  name: string;
  created_at?: string;
};

// Kiểu dữ liệu cho subcategories
export type Subcategory = {
  id: string;
  category_id: string;
  slug: string;
  name: string;
  created_at?: string;
};

// Kiểu dữ liệu cho articles
export type Article = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category_id: string;
  subcategory_id?: string;
  author: string;
  author_title?: string;
  author_bio?: string;
  author_image?: string;
  date: string;
  read_time: string;
  image_url: string;
  created_at?: string;
  updated_at?: string;
};

// Kiểu dữ liệu cho email_subscriptions
export type EmailSubscription = {
  id: string;
  email: string;
  name?: string;
  created_at?: string;
  status: "active" | "unsubscribed";
  source?: string;
  preferences?: {
    categories?: string[];
    frequency?: "daily" | "weekly";
  };
};

// Kiểu dữ liệu cho contact_messages
export type ContactMessage = {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: "new" | "read" | "replied" | "archived";
  created_at: string;
  updated_at: string;
  admin_notes?: string;
  reply_sent_at?: string;
};

// Service để làm việc với categories
export const categoriesService = {
  async getAll(): Promise<Category[]> {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Lỗi khi lấy danh sách categories:", error);
      return [];
    }

    return data || [];
  },

  async getBySlug(slug: string): Promise<Category | null> {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .eq("slug", slug)
      .single();

    if (error) {
      console.error(`Lỗi khi lấy category với slug ${slug}:`, error);
      return null;
    }

    return data;
  },
};

// Service để làm việc với subcategories
export const subcategoriesService = {
  async getAll(): Promise<Subcategory[]> {
    const { data, error } = await supabase
      .from("subcategories")
      .select("*")
      .order("name");

    if (error) {
      console.error("Lỗi khi lấy danh sách subcategories:", error);
      return [];
    }

    return data || [];
  },

  async getByCategoryId(categoryId: string): Promise<Subcategory[]> {
    const { data, error } = await supabase
      .from("subcategories")
      .select("*")
      .eq("category_id", categoryId)
      .order("name");

    if (error) {
      console.error(
        `Lỗi khi lấy subcategories cho category ${categoryId}:`,
        error
      );
      return [];
    }

    return data || [];
  },
};

// Service để làm việc với articles
export const articlesService = {
  async getAll(): Promise<Article[]> {
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .order("date", { ascending: false });

    if (error) {
      console.error("Lỗi khi lấy danh sách articles:", error);
      return [];
    }

    return data || [];
  },

  async getAllPaginated(page: number, pageSize: number): Promise<Article[]> {
    const offset = (page - 1) * pageSize;

    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .order("date", { ascending: false })
      .range(offset, offset + pageSize - 1);

    if (error) {
      console.error(`Lỗi khi lấy articles trang ${page}:`, error);
      return [];
    }

    return data || [];
  },

  async getCount(): Promise<number> {
    const { count, error } = await supabase
      .from("articles")
      .select("*", { count: "exact", head: true });

    if (error) {
      console.error("Lỗi khi đếm số lượng articles:", error);
      return 0;
    }

    return count || 0;
  },

  async getById(id: string): Promise<Article | null> {
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error(`Lỗi khi lấy article với id ${id}:`, error);
      return null;
    }

    return data;
  },

  async getByCategory(categoryId: string): Promise<Article[]> {
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .eq("category_id", categoryId)
      .order("date", { ascending: false });

    if (error) {
      console.error(`Lỗi khi lấy articles cho category ${categoryId}:`, error);
      return [];
    }

    return data || [];
  },

  async getBySubcategory(subcategoryId: string): Promise<Article[]> {
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .eq("subcategory_id", subcategoryId)
      .order("date", { ascending: false });

    if (error) {
      console.error(
        `Lỗi khi lấy articles cho subcategory ${subcategoryId}:`,
        error
      );
      return [];
    }

    return data || [];
  },

  async search(query: string): Promise<Article[]> {
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .or(
        `title.ilike.%${query}%,excerpt.ilike.%${query}%,content.ilike.%${query}%`
      )
      .order("date", { ascending: false });

    if (error) {
      console.error(`Lỗi khi tìm kiếm articles với query ${query}:`, error);
      return [];
    }

    return data || [];
  },

  async getArticlesFiltered({
    page = 1,
    pageSize = 10,
    categoryIds = [],
    subcategoryIds = [],
    searchQuery = "",
    countOnly = false,
  }: {
    page?: number;
    pageSize?: number;
    categoryIds?: string[];
    subcategoryIds?: string[];
    searchQuery?: string;
    countOnly?: boolean;
  }): Promise<{ data: Article[]; count: number }> {
    try {
      console.log("DEBUG - getArticlesFiltered called with:", {
        page,
        pageSize,
        categoryIds,
        subcategoryIds,
        searchQuery,
        countOnly,
      });

      // Kiểm tra xem categoryIds có hợp lệ không
      if (categoryIds.length > 0) {
        if (categoryIds.some((id) => !id || id.trim() === "")) {
          console.error(
            "DEBUG - ERROR: Phát hiện categoryId không hợp lệ:",
            categoryIds
          );
          categoryIds = categoryIds.filter((id) => id && id.trim() !== "");
        }
      }

      let query = supabase
        .from("articles")
        .select(countOnly ? "id" : "*", { count: "exact" });

      // Thêm điều kiện tìm kiếm nếu có
      if (searchQuery.trim() !== "") {
        query = query.or(
          `title.ilike.%${searchQuery}%,excerpt.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`
        );
      }

      // Thêm điều kiện lọc theo category
      if (categoryIds.length > 0) {
        console.log("DEBUG - Filtering by categoryIds:", categoryIds);

        // Nếu chỉ có một category ID
        if (categoryIds.length === 1) {
          query = query.eq("category_id", categoryIds[0]);
          console.log(
            `DEBUG - Using eq filter for single category: ${categoryIds[0]}`
          );
        } else {
          query = query.in("category_id", categoryIds);
          console.log(
            `DEBUG - Using in filter for multiple categories: ${categoryIds.join(
              ", "
            )}`
          );
        }
      }

      // Thêm điều kiện lọc theo subcategory
      if (subcategoryIds.length > 0) {
        // Nếu chỉ có một subcategory ID
        if (subcategoryIds.length === 1) {
          query = query.eq("subcategory_id", subcategoryIds[0]);
        } else {
          query = query.in("subcategory_id", subcategoryIds);
        }
      }

      // Sắp xếp bài viết
      query = query.order("date", { ascending: false });

      // Thêm phân trang nếu không phải là đếm
      if (!countOnly) {
        const offset = (page - 1) * pageSize;
        query = query.range(offset, offset + pageSize - 1);
      }

      // Thực hiện truy vấn
      const { data, error, count } = await query;

      if (error) {
        console.error("Lỗi khi lọc bài viết:", error);
        return { data: [], count: 0 };
      }

      // Kiểm tra xem kết quả có lọc đúng không, nếu không thì lọc thủ công
      let finalData = data || [];

      if (categoryIds.length > 0 && finalData.length > 0) {
        // Double-check lại kết quả để đảm bảo chỉ lấy bài viết có category_id đúng
        const originalCount = finalData.length;
        const filteredData = finalData.filter((item) =>
          categoryIds.includes(item.category_id)
        );

        if (filteredData.length !== originalCount) {
          console.error(
            "DEBUG - WARNING: Kết quả lọc từ Supabase không chính xác, phải lọc thủ công!"
          );
          console.log("DEBUG - Original count:", originalCount);
          console.log("DEBUG - After manual filtering:", filteredData.length);
          console.log(
            "DEBUG - Missing items:",
            finalData
              .filter((item) => !categoryIds.includes(item.category_id))
              .map((item) => ({
                id: item.id,
                title: item.title,
                category_id: item.category_id,
              }))
          );

          finalData = filteredData;
        }
      }

      console.log("DEBUG - getArticlesFiltered results:", {
        count,
        dataCount: finalData.length,
        categoryFilters:
          categoryIds.length > 0
            ? finalData.map((item) => ({
                id: item.id,
                title: item.title,
                category_id: item.category_id,
                matchesFilter: categoryIds.includes(item.category_id),
              }))
            : "No category filter applied",
      });

      return {
        data: finalData,
        count: categoryIds.length > 0 ? finalData.length : count || 0, // Điều chỉnh count nếu lọc thủ công
      };
    } catch (err) {
      console.error("Lỗi không mong muốn khi lọc bài viết:", err);
      return { data: [], count: 0 };
    }
  },
};

// Service để làm việc với email subscriptions
export const emailSubscriptionsService = {
  async subscribe(
    email: string,
    name?: string,
    source: string = "website"
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from("email_subscriptions")
        .insert([
          {
            email,
            name,
            source,
            status: "active",
            preferences: { categories: [], frequency: "weekly" },
          },
        ]);

      if (error) {
        // Check for unique constraint violation
        if (error.code === "23505") {
          return {
            success: false,
            error:
              "Email này đã đăng ký nhận tin. Vui lòng sử dụng email khác.",
          };
        }
        console.error("Lỗi khi đăng ký email subscription:", error);
        return {
          success: false,
          error: "Đã xảy ra lỗi khi đăng ký. Vui lòng thử lại sau.",
        };
      }

      return { success: true };
    } catch (err) {
      console.error("Lỗi không mong muốn khi đăng ký email:", err);
      return {
        success: false,
        error: "Đã xảy ra lỗi khi đăng ký. Vui lòng thử lại sau.",
      };
    }
  },

  async unsubscribe(
    email: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("email_subscriptions")
        .update({ status: "unsubscribed" })
        .eq("email", email);

      if (error) {
        console.error(`Lỗi khi hủy đăng ký email ${email}:`, error);
        return {
          success: false,
          error: "Đã xảy ra lỗi khi hủy đăng ký. Vui lòng thử lại sau.",
        };
      }

      return { success: true };
    } catch (err) {
      console.error("Lỗi không mong muốn khi hủy đăng ký email:", err);
      return {
        success: false,
        error: "Đã xảy ra lỗi khi hủy đăng ký. Vui lòng thử lại sau.",
      };
    }
  },

  async getAll(): Promise<EmailSubscription[]> {
    const { data, error } = await supabase
      .from("email_subscriptions")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Lỗi khi lấy danh sách email subscriptions:", error);
      return [];
    }

    return data || [];
  },

  async getActive(): Promise<EmailSubscription[]> {
    const { data, error } = await supabase
      .from("email_subscriptions")
      .select("*")
      .eq("status", "active")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Lỗi khi lấy danh sách email subscriptions active:", error);
      return [];
    }

    return data || [];
  },

  async updatePreferences(
    email: string,
    preferences: { categories?: string[]; frequency?: "daily" | "weekly" }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("email_subscriptions")
        .update({ preferences })
        .eq("email", email);

      if (error) {
        console.error(
          `Lỗi khi cập nhật preferences cho email ${email}:`,
          error
        );
        return {
          success: false,
          error: "Đã xảy ra lỗi khi cập nhật tùy chọn. Vui lòng thử lại sau.",
        };
      }

      return { success: true };
    } catch (err) {
      console.error("Lỗi không mong muốn khi cập nhật preferences:", err);
      return {
        success: false,
        error: "Đã xảy ra lỗi khi cập nhật tùy chọn. Vui lòng thử lại sau.",
      };
    }
  },
};

// Service để làm việc với contact messages
export const contactMessagesService = {
  async send(
    name: string,
    email: string,
    subject: string,
    message: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.from("contact_messages").insert([
        {
          name,
          email,
          subject,
          message,
          status: "new",
        },
      ]);

      if (error) {
        console.error("Lỗi khi gửi contact message:", error);
        return {
          success: false,
          error: "Đã xảy ra lỗi khi gửi tin nhắn. Vui lòng thử lại sau.",
        };
      }

      return { success: true };
    } catch (err) {
      console.error("Lỗi không mong muốn khi gửi contact message:", err);
      return {
        success: false,
        error: "Đã xảy ra lỗi khi gửi tin nhắn. Vui lòng thử lại sau.",
      };
    }
  },

  async getAll(): Promise<ContactMessage[]> {
    const { data, error } = await supabase
      .from("contact_messages")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Lỗi khi lấy danh sách contact messages:", error);
      return [];
    }

    return data || [];
  },

  async getById(id: string): Promise<ContactMessage | null> {
    const { data, error } = await supabase
      .from("contact_messages")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error(`Lỗi khi lấy contact message với id ${id}:`, error);
      return null;
    }

    return data;
  },

  async updateStatus(
    id: string,
    status: "new" | "read" | "replied" | "archived"
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("contact_messages")
        .update({ status })
        .eq("id", id);

      if (error) {
        console.error(`Lỗi khi cập nhật status cho message ${id}:`, error);
        return {
          success: false,
          error: "Đã xảy ra lỗi khi cập nhật trạng thái. Vui lòng thử lại sau.",
        };
      }

      return { success: true };
    } catch (err) {
      console.error("Lỗi không mong muốn khi cập nhật status:", err);
      return {
        success: false,
        error: "Đã xảy ra lỗi khi cập nhật trạng thái. Vui lòng thử lại sau.",
      };
    }
  },

  async addNotes(
    id: string,
    admin_notes: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("contact_messages")
        .update({ admin_notes })
        .eq("id", id);

      if (error) {
        console.error(`Lỗi khi thêm ghi chú cho message ${id}:`, error);
        return {
          success: false,
          error: "Đã xảy ra lỗi khi thêm ghi chú. Vui lòng thử lại sau.",
        };
      }

      return { success: true };
    } catch (err) {
      console.error("Lỗi không mong muốn khi thêm ghi chú:", err);
      return {
        success: false,
        error: "Đã xảy ra lỗi khi thêm ghi chú. Vui lòng thử lại sau.",
      };
    }
  },

  async markAsReplied(
    id: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("contact_messages")
        .update({
          status: "replied",
          reply_sent_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) {
        console.error(`Lỗi khi đánh dấu đã trả lời message ${id}:`, error);
        return {
          success: false,
          error: "Đã xảy ra lỗi khi cập nhật trạng thái. Vui lòng thử lại sau.",
        };
      }

      return { success: true };
    } catch (err) {
      console.error("Lỗi không mong muốn khi đánh dấu đã trả lời:", err);
      return {
        success: false,
        error: "Đã xảy ra lỗi khi cập nhật trạng thái. Vui lòng thử lại sau.",
      };
    }
  },

  async delete(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("contact_messages")
        .delete()
        .eq("id", id);

      if (error) {
        console.error(`Lỗi khi xóa message ${id}:`, error);
        return {
          success: false,
          error: "Đã xảy ra lỗi khi xóa tin nhắn. Vui lòng thử lại sau.",
        };
      }

      return { success: true };
    } catch (err) {
      console.error("Lỗi không mong muốn khi xóa tin nhắn:", err);
      return {
        success: false,
        error: "Đã xảy ra lỗi khi xóa tin nhắn. Vui lòng thử lại sau.",
      };
    }
  },
};
